import type { UseMutationResult } from '@tanstack/react-query'
import { BoolIcon } from '@/components/common/BoolIcon'
import { useBanksAdmin, useCreateBank, useUpdateBank, useDeleteBank } from '@/features/contract/financing/hooks'
import type { Bank } from '@/features/contract/financing/types'
import type { DictionaryPanelConfig } from '../DictionaryPanel'

const APPEND_SORT_ORDER = 1_000_000

export const banksConfig: DictionaryPanelConfig<Bank> = {
  title: 'Banki',
  description: 'Banki dla kredytów (finansowanie umowy).',
  addLabel: 'Dodaj bank',
  useList: useBanksAdmin,
  useCreate: () => {
    const m = useCreateBank()
    return {
      ...m,
      mutate: (payload: Record<string, unknown>, opts?: Parameters<typeof m.mutate>[1]) =>
        m.mutate(
          {
            code: String(payload['code'] ?? ''),
            labelPl: String(payload['labelPl'] ?? ''),
            sortOrder: APPEND_SORT_ORDER,
            isActive: Boolean(payload['isActive'] ?? true),
          },
          opts,
        ),
    } as unknown as UseMutationResult<Bank, Error, Record<string, unknown>>
  },
  useUpdate: () => {
    const m = useUpdateBank()
    return {
      ...m,
      mutate: (
        vars: { code: string; patch: Record<string, unknown> },
        opts?: Parameters<typeof m.mutate>[1],
      ) =>
        m.mutate(
          {
            code: vars.code,
            payload: {
              labelPl: String(vars.patch['labelPl'] ?? ''),
              sortOrder: Number(vars.patch['sortOrder'] ?? APPEND_SORT_ORDER),
              isActive: Boolean(vars.patch['isActive'] ?? true),
            },
          },
          opts,
        ),
    } as unknown as UseMutationResult<Bank, Error, { code: string; patch: Record<string, unknown> }>
  },
  useDeactivate: useDeleteBank,
  columns: [
    { key: 'code', header: 'Kod', className: 'font-mono text-slate-500 w-40' },
    { key: 'labelPl', header: 'Etykieta' },
    { key: 'isActive', header: 'Aktywne', render: (b) => BoolIcon({ value: b.isActive }) },
  ],
  formFields: [
    { key: 'code', label: 'Kod', type: 'code', required: true },
    { key: 'labelPl', label: 'Etykieta', type: 'text', required: true },
    { key: 'isActive', label: 'Aktywne', type: 'boolean' },
  ],
}
